/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const mobileQuery = window.matchMedia('(max-width: 749px)');

/**
 * Whether the browser plays HLS without a library (Safari, iOS, Android and
 * current Chrome/Edge). Shopify's HLS stream adapts its quality to the
 * connection; its single mp4 rendition is 1080p at 7.2 Mbps and stalls
 * whenever the CDN delivers less than that, so the stream comes first.
 */
const supportsHls = document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';

/** Controls fade out after this long without pointer or keyboard activity while playing. */
const CONTROLS_IDLE_MS = 3000;

/** Arrow keys skip this many seconds. */
const SEEK_STEP_SECONDS = 5;

/** The range input's resolution; 1000 steps keeps scrubbing smooth on long films. */
const PROGRESS_STEPS = 1000;

/**
 * @param {number} seconds
 * @returns {string} m:ss, or h:mm:ss from an hour on.
 */
function formatTime(seconds) {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, '0');

  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`;
}

/**
 * "Watch video" player for campaign banners (blocks/watch-video.liquid).
 *
 * Opens the film in a full-viewport modal dialog with a custom control bar.
 * Open/close, Escape, focus and scroll lock come from DialogComponent. The film
 * always starts muted from the beginning, so play() needs no user-gesture
 * allowance. Videos already playing in the same section (the banner's
 * background loop) are paused while the player is open and resumed after.
 *
 * Full screen uses the Fullscreen API on the player so the custom controls stay
 * visible; iPhone Safari has no element full screen, so it falls back to the
 * video's native full-screen player there.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} player
 * @property {HTMLVideoElement} video
 * @property {HTMLButtonElement} playButton
 * @property {HTMLButtonElement} muteButton
 * @property {HTMLInputElement} progress
 * @property {HTMLElement} time
 * @property {HTMLButtonElement} fullscreenButton
 *
 * @extends {DialogComponent}
 */
class CampaignVideo extends DialogComponent {
  requiredRefs = ['dialog', 'player', 'video', 'playButton', 'muteButton', 'progress', 'time', 'fullscreenButton'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #idleTimer;

  /** @type {HTMLVideoElement[]} */
  #pausedSectionVideos = [];

  /** Whether the controls were hidden when the current tap or click started. */
  #controlsHiddenOnPointerDown = false;

  connectedCallback() {
    super.connectedCallback();

    const { video, player, fullscreenButton } = this.refs;

    for (const type of ['play', 'pause', 'ended']) video.addEventListener(type, this.#syncPlaying);
    for (const type of ['timeupdate', 'durationchange', 'loadedmetadata']) {
      video.addEventListener(type, this.#syncProgress);
    }
    video.addEventListener('volumechange', this.#syncMuted);

    player.addEventListener('pointermove', this.#showControls);
    player.addEventListener('pointerdown', this.#handlePointerDown);
    player.addEventListener('focusin', this.#showControls);
    player.addEventListener('keydown', this.#handleShortcut);

    document.addEventListener('fullscreenchange', this.#syncFullscreen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClosed);

    fullscreenButton.hidden = !player.requestFullscreen && !('webkitEnterFullscreen' in video);

    this.#syncPlaying();
    this.#syncMuted();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('fullscreenchange', this.#syncFullscreen);
    clearTimeout(this.#idleTimer);
  }

  /**
   * Opens the player and plays the film from the start, muted. The source is
   * chosen at open time (HLS stream or mp4, mobile or desktop film), so a
   * resize between opens picks again.
   */
  open() {
    const { video } = this.refs;
    const { hlsDesktop, hlsMobile, srcDesktop, srcMobile, posterDesktop, posterMobile } = video.dataset;
    const mobile = mobileQuery.matches;
    const hls = supportsHls ? (mobile && hlsMobile) || hlsDesktop : '';
    const src = hls || (mobile && srcMobile) || srcDesktop;

    if (!src) return;

    if (video.dataset.loadedSrc !== src) {
      video.poster = (mobile && posterMobile) || posterDesktop || '';
      video.src = src;
      video.dataset.loadedSrc = src;
    } else {
      video.currentTime = 0;
    }

    video.muted = true;
    this.#pauseSectionVideos();
    this.showDialog();
    video.play().catch(() => {});
    this.#showControls();
  }

  /**
   * Closes the player. The film is paused straight away instead of when the
   * closing animation ends, so no sound carries over the fade.
   */
  close() {
    this.refs.video.pause();
    this.closeDialog();
  }

  togglePlay() {
    const { video } = this.refs;

    if (video.paused || video.ended) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  toggleMute() {
    const { video } = this.refs;

    video.muted = !video.muted;
  }

  seek() {
    const { video, progress } = this.refs;

    if (!Number.isFinite(video.duration)) return;

    video.currentTime = (Number(progress.value) / PROGRESS_STEPS) * video.duration;
    this.#syncProgress();
  }

  toggleFullscreen() {
    const { player, video } = this.refs;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }

    if (player.requestFullscreen) {
      player.requestFullscreen().catch(() => {});
      return;
    }

    /** @type {HTMLVideoElement & { webkitEnterFullscreen?: () => void }} */ (video).webkitEnterFullscreen?.();
  }

  /**
   * A tap on the picture while the controls are hidden only brings them back
   * (touch screens have no hover to reveal them); otherwise it toggles playback.
   */
  handleVideoClick() {
    if (this.#controlsHiddenOnPointerDown) return;

    this.togglePlay();
  }

  #handlePointerDown = () => {
    this.#controlsHiddenOnPointerDown = this.refs.player.hasAttribute('data-controls-hidden');
    this.#showControls();
  };

  /** Shows the controls and, while the film plays, schedules hiding them again. */
  #showControls = () => {
    const { player, video } = this.refs;

    player.removeAttribute('data-controls-hidden');
    clearTimeout(this.#idleTimer);

    if (video.paused) return;

    this.#idleTimer = setTimeout(() => {
      // Keep them visible while a control has keyboard focus.
      if (video.paused || player.querySelector(':focus-visible')) return;

      player.setAttribute('data-controls-hidden', '');
    }, CONTROLS_IDLE_MS);
  };

  #syncPlaying = () => {
    const { player, video, playButton } = this.refs;
    const playing = !video.paused && !video.ended;

    player.toggleAttribute('data-playing', playing);
    playButton.setAttribute('aria-label', (playing ? this.dataset.labelPause : this.dataset.labelPlay) ?? '');
    this.#showControls();
  };

  #syncMuted = () => {
    const { player, video, muteButton } = this.refs;

    player.toggleAttribute('data-muted', video.muted);
    muteButton.setAttribute('aria-label', (video.muted ? this.dataset.labelUnmute : this.dataset.labelMute) ?? '');
  };

  #syncProgress = () => {
    const { video, progress, time } = this.refs;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const ratio = duration ? Math.min(video.currentTime / duration, 1) : 0;
    const text = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;

    progress.value = String(Math.round(ratio * PROGRESS_STEPS));
    progress.style.setProperty('--progress', `${ratio * 100}%`);
    progress.setAttribute('aria-valuetext', text);
    time.textContent = text;
  };

  #syncFullscreen = () => {
    const { player, fullscreenButton } = this.refs;
    const fullscreen = document.fullscreenElement === player;

    player.toggleAttribute('data-fullscreen', fullscreen);
    fullscreenButton.setAttribute(
      'aria-label',
      (fullscreen ? this.dataset.labelExitFullscreen : this.dataset.labelEnterFullscreen) ?? '',
    );
  };

  /** @param {KeyboardEvent} event */
  #handleShortcut = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const target = /** @type {Element} */ (event.target);
    const onButton = target instanceof HTMLButtonElement;
    const onProgress = target === this.refs.progress;
    const { video } = this.refs;

    switch (event.key) {
      case ' ':
      case 'k':
        // A focused button already activates on Space.
        if (onButton && event.key === ' ') return;
        event.preventDefault();
        this.togglePlay();
        break;
      case 'm':
        this.toggleMute();
        break;
      case 'f':
        this.toggleFullscreen();
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        // The range input moves itself with the arrow keys.
        if (onProgress || !Number.isFinite(video.duration)) return;
        event.preventDefault();
        video.currentTime += event.key === 'ArrowRight' ? SEEK_STEP_SECONDS : -SEEK_STEP_SECONDS;
        break;
      default:
        return;
    }

    this.#showControls();
  };

  /** Pauses other videos playing in this section, such as the banner's background loop. */
  #pauseSectionVideos() {
    const section = this.closest('.shopify-section') ?? document;

    this.#pausedSectionVideos = [...section.querySelectorAll('video')].filter(
      (other) => other !== this.refs.video && !other.paused,
    );

    for (const other of this.#pausedSectionVideos) other.pause();
  }

  #handleClosed = () => {
    const { video } = this.refs;

    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});

    video.pause();
    clearTimeout(this.#idleTimer);

    for (const other of this.#pausedSectionVideos) other.play().catch(() => {});
    this.#pausedSectionVideos = [];
  };
}

if (!customElements.get('campaign-video')) customElements.define('campaign-video', CampaignVideo);
